import { Linkedin, Instagram } from "lucide-react";
import Reveal from "@/components/Reveal";
import CardSlider from "@/components/CardSlider";
import { CONTACT } from "@/config";

export const TEAM = [
    {
        name: "Ulfath Pinjari",
        role: "Founder & Lead Data Analyst",
        desc: "Leads data analytics projects at Analytrova, helping businesses transform raw data into meaningful insights and make smart, data-driven decisions.",
        skills: ["Data Analytics", "Advanced Excel", "Dashboard Creation", "Data Cleaning", "Business Insights"],
        initials: "UP",
        photo: "/team/ulfath.jpg",
        linkedin: "https://www.linkedin.com/in/ulfath-nadaf-a02a6337b",
    },
    {
        name: "Shelly Sharma",
        role: "Co-Founder & Data Analyst",
        desc: "Works on data analysis, reporting and business insights to help clients understand their data and make informed decisions.",
        skills: ["Data Analysis", "Reporting", "Data Insights", "Excel", "Business Analysis"],
        initials: "SS",
        photo: "/team/shelly.jpg",
        linkedin: "https://www.linkedin.com/in/sharma-shellys",
    },
    {
        name: "Ayesha Imran Khan",
        role: "Co-Founder & Website & Digital Designer",
        desc: "Leads website design and creative digital solutions that help businesses build a professional online presence.",
        skills: ["Website Design", "Website Development", "Logo & Branding", "AI Video & Ads", "Social Media Creatives"],
        initials: "AK",
        photo: "/team/ayesha.jpg",
    },
];

export default function Team() {
    return (
        <section id="team" className="py-20 lg:py-28" data-testid="team-section">
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <Reveal className="text-center max-w-2xl mx-auto">
                    <div className="flex items-center justify-center gap-3 mb-4">
                        <span className="h-px w-10 bg-gold-deep" />
                        <span className="text-[11px] font-semibold tracking-[0.34em] text-burgundy">OUR TEAM</span>
                        <span className="h-px w-10 bg-gold-deep" />
                    </div>
                    <h2 className="text-4xl sm:text-5xl text-charcoal">Meet Our Team</h2>
                    <p className="mt-4 text-charcoal/65">The people behind Analytrova</p>
                </Reveal>

                <CardSlider
                    testPrefix="team"
                    items={TEAM.map((m, i) => (
                        <Reveal key={m.name} delay={(i % 3) * 0.08} className="h-full">
                            <div
                                className="group card-lift h-full bg-cream-soft border border-burgundy/10 rounded-2xl overflow-hidden flex flex-col"
                                data-testid={`team-card-${i}`}
                            >
                                {/* photo / monogram placeholder */}
                                <div className="relative h-64 bg-olive/25 flex items-center justify-center overflow-hidden">
                                    {m.photo ? (
                                        <img src={m.photo} alt={m.name} className="w-full h-full object-cover object-top" />
                                    ) : (
                                        <span className="w-24 h-24 rounded-full bg-cream border border-gold-deep/60 flex items-center justify-center font-serif text-3xl text-burgundy tracking-wide">
                                            {m.initials}
                                        </span>
                                    )}
                                </div>

                                <div className="p-7 flex flex-col grow">
                                    <h3 className="text-xl text-charcoal">{m.name}</h3>
                                    {m.role && (
                                        <p className="mt-1 text-[11px] font-semibold tracking-[0.18em] uppercase text-burgundy">{m.role}</p>
                                    )}
                                    <p className="mt-3 text-sm leading-relaxed text-charcoal/65 grow">{m.desc}</p>
                                    <div className="mt-5 flex flex-wrap gap-1.5">
                                        {m.skills.map((s) => (
                                            <span key={s} className="text-[11px] px-2.5 py-1 rounded-full bg-olive/25 text-charcoal/80">
                                                {s}
                                            </span>
                                        ))}
                                    </div>
                                </div>

                                <div className="border-t border-burgundy/10 px-7 py-4 flex gap-3">
                                    <a
                                        href={m.linkedin || CONTACT.linkedin}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label={`${m.name} on LinkedIn`}
                                        className="w-9 h-9 rounded-full border border-burgundy/20 text-burgundy flex items-center justify-center transition-colors hover:bg-burgundy hover:text-cream"
                                        data-testid={`team-linkedin-${i}`}
                                    >
                                        <Linkedin className="w-4 h-4" />
                                    </a>
                                    <a
                                        href={m.instagram || CONTACT.instagram}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label={`${m.name} on Instagram`}
                                        className="w-9 h-9 rounded-full border border-burgundy/20 text-burgundy flex items-center justify-center transition-colors hover:bg-burgundy hover:text-cream"
                                        data-testid={`team-instagram-${i}`}
                                    >
                                        <Instagram className="w-4 h-4" />
                                    </a>
                                </div>
                            </div>
                        </Reveal>
                    ))}
                />
            </div>
        </section>
    );
}
